import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ObliqueIcon from "@/components/icons/ObliqueIcon";

/**
 * Collapsible Section Header
 */
const CollapsibleSection = ({
  label,
  isCollapsed,
  defaultExpanded = true,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  if (isCollapsed) return children;
  return (
    <div className="mb-1 mt-1">
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between cursor-pointer hover-bg-subtle rounded-md my-[0px] mx-1 [padding:8px_10px_4px] [user-select:none] [transition:background_0.2s]"
      >
        <div className="uppercase text-[var(--text-tertiary)] text-[9.5px] font-bold tracking-[0.06em]">
          {label}
        </div>
        <motion.div
          animate={{
            rotate: isExpanded ? 90 : 0,
          }}
          transition={{
            duration: 0.15,
          }}
          className="text-[var(--text-tertiary)] opacity-50"
        >
          <ObliqueIcon name="chevron_right" size={14} />
        </motion.div>
      </div>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{
              height: 0,
              opacity: 0,
            }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
            }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Single Nav Item — Oblique-Style flat item
 */
const NavItem = ({
  to,
  icon,
  label,
  isCollapsed,
  badge,
  iconRenderer,
  end = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const isActuallyActive = React.useMemo(() => {
    const [path, search] = to.split("?");
    if (search) {
      return location.pathname === path && location.search === `?${search}`;
    }
    if (end) {
      return location.pathname === path && !location.search;
    }
    return location.pathname.startsWith(path);
  }, [location.pathname, location.search, to, end]);
  return (
    <NavLink
      to={to}
      end={end}
      style={() => ({
        display: "flex",
        alignItems: "center",
        justifyContent: isCollapsed ? "center" : "flex-start",
        borderRadius: "6px",
        margin: isCollapsed ? "1px 6px" : "1px 8px",
        padding: isCollapsed ? "6px 0" : "6px 10px",
        minHeight: "32px",
        position: "relative",
        background:
          isHovered && !isActuallyActive ? "var(--bg-hover)" : "transparent",
        color: isActuallyActive
          ? "var(--text-primary)"
          : "var(--text-secondary)",
        gap: "10px",
        textDecoration: "none",
        transition: "all 150ms ease",
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isActuallyActive && (
        <motion.div
          layoutId="activeQualityTabIndicator"
          className="absolute bg-[var(--bg-muted)] rounded-md z-[0] [inset:0]"
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          transition={{
            type: "spring",
            stiffness: 350,
            damping: 30,
          }}
        />
      )}
      <div
        className="flex items-center relative w-full gap-[10px] z-[1] "
        style={{
          justifyContent: isCollapsed ? "center" : "flex-start",
        }}
      >
        {iconRenderer ? (
          <div
            className="flex shrink-0 "
            style={{
              color: isActuallyActive
                ? "var(--accent-qms, #059669)"
                : "currentColor",
            }}
          >
            {typeof iconRenderer === "function"
              ? iconRenderer(isActuallyActive)
              : iconRenderer}
          </div>
        ) : (
          <ObliqueIcon
            name={icon}
            size={22}
            strokeScale={1.1}
            isActive={isActuallyActive}
            filled={isActuallyActive}
            className="shrink-0 [transition:color_150ms_ease] "
            style={{
              color: isActuallyActive
                ? "var(--accent-qms, #059669)"
                : "var(--text-tertiary)",
            }}
          />
        )}
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{
                width: 0,
                opacity: 0,
              }}
              animate={{
                width: "auto",
                opacity: 1,
              }}
              exit={{
                width: 0,
                opacity: 0,
              }}
              className="flex items-center flex-1 overflow-hidden whitespace-nowrap"
              transition={{
                duration: 0.2,
                ease: "easeInOut",
              }}
            >
              <span
                className="flex-1 text-[13.5px] "
                style={{
                  fontWeight: isActuallyActive ? 700 : 500,
                }}
              >
                {label}
              </span>
              {badge > 0 && (
                <span
                  className="font-semibold text-[11px] py-0.5 px-[7px] rounded-[999px] ml-auto "
                  style={{
                    color: isActuallyActive
                      ? "var(--accent-qms, #059669)"
                      : "var(--text-tertiary)",
                    background: isActuallyActive
                      ? "rgba(5, 150, 105, 0.12)"
                      : "var(--bg-subtle)",
                  }}
                >
                  {badge}
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </NavLink>
  );
};
const ExpandableGroup = ({
  title,
  icon,
  iconRenderer,
  items,
  basePath,
  isCollapsed,
  badge = 0,
}) => {
  const location = useLocation();
  const hideTimerRef = useRef(null);
  const containerRef = useRef(null);
  const isSectionActive =
    items.some(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path?.split("?")[0]),
    ) || location.pathname.startsWith(basePath);
  const [isExpanded, setIsExpanded] = useState(isSectionActive);
  const [showFlyout, setShowFlyout] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState({
    top: 0,
    left: 0,
  });
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (isSectionActive) setIsExpanded(true);
  }, [location.pathname, isSectionActive]);
  useEffect(() => {
    setShowFlyout(false);
  }, [location.pathname]);
  const handleMouseEnter = () => {
    setIsHovered(true);
    if (!isCollapsed) return;
    clearTimeout(hideTimerRef.current);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setFlyoutPos({
        top: rect.top,
        left: rect.right + 8,
      });
    }
    setShowFlyout(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    if (!isCollapsed) return;
    hideTimerRef.current = setTimeout(() => setShowFlyout(false), 150);
  };
  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Group header */}
      <div
        onClick={() => {
          if (!isCollapsed) setIsExpanded(!isExpanded);
        }}
        className="flex items-center cursor-pointer gap-[10px] rounded-md min-h-8 [transition:all_150ms_ease] "
        style={{
          justifyContent: isCollapsed ? "center" : "flex-start",
          margin: isCollapsed ? "1px 6px" : "1px 8px",
          padding: isCollapsed ? "6px 0" : "6px 10px",
          background:
            isSectionActive && !isExpanded
              ? "var(--bg-muted)"
              : isHovered
                ? "var(--bg-hover)"
                : "transparent",
          color:
            isSectionActive && !isExpanded
              ? "var(--text-primary)"
              : "var(--text-secondary)",
        }}
      >
        {iconRenderer ? (
          <div
            className="flex shrink-0 "
            style={{
              color:
                isSectionActive && !isExpanded
                  ? "var(--accent-qms, #059669)"
                  : "currentColor",
            }}
          >
            {typeof iconRenderer === "function"
              ? iconRenderer(isSectionActive && !isExpanded)
              : iconRenderer}
          </div>
        ) : (
          <ObliqueIcon
            name={icon}
            size={22}
            strokeScale={1.1}
            isActive={isSectionActive && !isExpanded}
            className="shrink-0 [transition:color_150ms_ease] "
            style={{
              color:
                isSectionActive && !isExpanded
                  ? "var(--accent-qms, #059669)"
                  : "var(--text-tertiary)",
            }}
          />
        )}

        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{
                width: 0,
                opacity: 0,
              }}
              animate={{
                width: "auto",
                opacity: 1,
              }}
              exit={{
                width: 0,
                opacity: 0,
              }}
              className="flex items-center flex-1 overflow-hidden whitespace-nowrap"
              transition={{
                duration: 0.2,
                ease: "easeInOut",
              }}
            >
              <span
                className="flex-1 overflow-hidden whitespace-nowrap text-[13.5px] text-ellipsis "
                style={{
                  fontWeight: isSectionActive && !isExpanded ? 700 : 500,
                }}
              >
                {title}
              </span>
              {badge > 0 && (
                <span
                  className="font-semibold text-[11px] py-0.5 px-[7px] rounded-[999px] mr-1 "
                  style={{
                    color:
                      isSectionActive && !isExpanded
                        ? "var(--accent-qms, #059669)"
                        : "var(--text-tertiary)",
                    background:
                      isSectionActive && !isExpanded
                        ? "rgba(5, 150, 105, 0.12)"
                        : "var(--bg-subtle)",
                  }}
                >
                  {badge}
                </span>
              )}
              <motion.div
                animate={{
                  rotate: isExpanded ? 90 : 0,
                }}
                transition={{
                  duration: 0.15,
                }}
                className="flex items-center shrink-0 [transition:opacity_0.2s] "
                style={{
                  opacity: isHovered || isExpanded ? 0.6 : 0,
                }}
              >
                <ObliqueIcon name="chevron_right" size={16} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Expanded sub-items — Apple Notes Style */}
      <AnimatePresence>
        {!isCollapsed && isExpanded && (
          <motion.div
            initial={{
              height: 0,
              opacity: 0,
            }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
            }}
            transition={{
              duration: 0.2,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="overflow-hidden"
          >
            <div className="flex flex-col pl-[28px] pr-2 mt-0.5 mb-1.5 gap-0.5">
              {items.map((item, index) => (
                <NavLink
                  key={`${item.path}-${index}`}
                  to={item.path}
                  style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none",
                    color: isActive
                      ? "var(--text-primary)"
                      : "var(--text-secondary)",
                    fontWeight: isActive ? "700" : "500",
                    padding: "6px 10px",
                    borderRadius: "8px",
                    background: isActive ? "var(--bg-muted)" : "transparent",
                    fontSize: "13px",
                    transition: "all 150ms ease",
                    gap: "10px",
                    minHeight: "32px",
                  })}
                >
                  {({ isActive }) => (
                    <>
                      {item.icon && (
                        <ObliqueIcon
                          name={item.icon}
                          filled={isActive}
                          size={16}
                          className="shrink-0 "
                          style={{
                            color: isActive
                              ? "var(--accent-qms, #059669)"
                              : "var(--text-tertiary)",
                          }}
                        />
                      )}
                      <span className="flex-1 overflow-hidden whitespace-nowrap text-ellipsis">
                        {item.label}
                      </span>
                      {item.count > 0 && (
                        <span
                          className="font-semibold text-[11px] py-[1px] px-1.5 rounded-[999px] "
                          style={{
                            color: isActive
                              ? "var(--accent-qms, #059669)"
                              : "var(--text-tertiary)",
                            background: isActive
                              ? "rgba(5, 150, 105, 0.12)"
                              : "var(--bg-subtle)",
                          }}
                        >
                          {item.count}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed Hover Flyout */}
      <AnimatePresence>
        {isCollapsed && showFlyout && (
          <FlyoutMenu
            title={title}
            items={items}
            position={flyoutPos}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Flyout Menu for Collapsed Sidebar — shown via portal
 */
const FlyoutMenu = ({ title, items, position, onMouseEnter, onMouseLeave }) => {
  const flyoutHeight = items.length * 34 + 36;
  const centeredTop = Math.max(12, position.top + (24 - flyoutHeight / 2));
  return createPortal(
    <motion.div
      initial={{
        opacity: 0,
        x: -8,
        scale: 0.97,
      }}
      animate={{
        opacity: 1,
        x: 0,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        x: -4,
        scale: 0.97,
        transition: {
          duration: 0.15,
        },
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl fixed w-[200px] p-1.5 z-[var(--z-base)] shadow-[0_16px_48px_rgba(0,0,0,0.18),_0_0_0_1px_rgba(255,255,255,0.04)] [left:76px] "
      style={{
        top: `${centeredTop}px`,
      }}
    >
      <div className="font-semibold uppercase text-[var(--text-tertiary)] border-b border-[var(--border)] text-[9.5px] mb-1 tracking-[0.04em] [padding:4px_10px_6px]">
        {title}
      </div>
      <div className="flex flex-col gap-[1px]">
        {items.map((item, idx) => (
          <NavLink
            key={item.path + idx}
            to={item.path}
            style={({ isActive }) => ({
              display: "flex",
              alignItems: "center",
              gap: "8px",
              textDecoration: "none",
              padding: "6px 10px",
              borderRadius: "8px",
              background: isActive ? "var(--bg-muted)" : "transparent",
              color: isActive ? "var(--accent)" : "var(--text-secondary)",
              fontSize: "12.5px",
              fontWeight: isActive ? "600" : "400",
              transition: "all 0.12s",
            })}
          >
            {({ isActive }) => (
              <>
                <ObliqueIcon
                  name={item.icon || "circle"}
                  filled={isActive}
                  size={18}
                  style={{
                    opacity: isActive ? 1 : 0.5,
                  }}
                  className=""
                />
                <span className="flex-1">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </motion.div>,
    document.body,
  );
};

/**
 * Quality Module Sidebar — Redesigned (Oblique + Linear style)
{/* 
 * Structure:
 * ─── CORE ───
 *   Dashboard
 *   Escalations
 *   Sales Audits
 * ─── AUDITS ───
 *   BAU Audits (Call, Lead/HubSpot, Email, Chat, Video)
 * ─── OPERATIONS ───
 *   Orders
 *   Campaigns
 *   Feedback
 *   Planner
 * ─── INTELLIGENCE ───
 *   YODA (The Wall, Playlists, Weapons, Compliance)
 */
const QualitySidebar = ({
  isCollapsed,
  getNavItemStyle,
  badgeStyle,
  auditCounts = {},
  ThemeCalendarIcon,
}) => {
  const location = useLocation();
  return (
    <div className="flex flex-col gap-0.5 mb-2">
      {/* Primary Navigation - Flat, clean, non-bifurcated */}
      <NavItem
        to="/quality"
        end
        icon="dashboard"
        label="Overview"
        isCollapsed={isCollapsed}
      />
      <NavItem
        to="/quality/sales-audits"
        icon="payments"
        label="Sales Audits"
        isCollapsed={isCollapsed}
        badge={auditCounts.deals}
      />
      <NavItem
        to="/quality/audits"
        icon="forum"
        label="Interactions"
        isCollapsed={isCollapsed}
        badge={
          (auditCounts.calls || 0) + (auditCounts.emails || 0) + (auditCounts.chats || 0) + (auditCounts.meetings || 0) > 0
            ? (auditCounts.calls || 0) + (auditCounts.emails || 0) + (auditCounts.chats || 0) + (auditCounts.meetings || 0)
            : null
        }
      />
      <NavItem
        to="/quality/leads"
        icon="person_search"
        label="Lead Audits"
        isCollapsed={isCollapsed}
        badge={auditCounts.leads}
      />
      <NavItem
        to="/quality/analytics"
        icon="insights"
        label="QA Analytics"
        isCollapsed={isCollapsed}
      />
      <NavItem
        to="/quality/calibration"
        icon="balance"
        label="Calibration"
        isCollapsed={isCollapsed}
      />
      <NavItem
        to="/quality/escalations"
        icon="report_problem"
        label="Escalations & RCAs"
        isCollapsed={isCollapsed}
        badge={auditCounts.escalations}
      />
      <NavItem
        to="/quality/coaching"
        icon="book_ribbon"
        label="Coaching & Feedbacks"
        isCollapsed={isCollapsed}
      />
      <NavItem
        to="/quality/people"
        icon="groups"
        label="People"
        isCollapsed={isCollapsed}
      />
      <NavItem
        to="/quality/sops"
        icon="menu_book"
        label="SOP Library"
        isCollapsed={isCollapsed}
      />
      <NavItem
        to="/quality/compliance"
        icon="add_to_drive"
        label="Compliance Drive"
        isCollapsed={isCollapsed}
      />

      <div className="flex-1" />
    </div>
  );
};
export default QualitySidebar;
